import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  FormLabel,
  Textarea,
  Text,
  Heading,
  Button,
  useToast,
  useColorModeValue,
  Flex,
  VStack,
  Checkbox,
  Link,
  HStack,
} from "@chakra-ui/react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { GdriveTenantCredentials } from "../Common/types";
import { useTenant } from "../Routers/TenantContextProvider";

interface TokenPayload {
  sub: string;
  role_id: number;
  tenant_id: number;
  exp?: number;
}

const TenantCMSMapping = () => {

  const { tenant, loading, error } = useTenant();

  const cardbg = useColorModeValue("gray.50", "gray.700");
  const [selectedTenant, setSelectedTenant] = useState<number | null>(null);
  const [gdrivetenantcredentials, setGdriveTenantCredentials] = useState<GdriveTenantCredentials>({
    id: 0,
    tenant_id: 0,
    gdrive_credentials: '',
    is_active: true,
    created_at: new Date(),
    updated_at: new Date(),
  });
  const toast = useToast();

  const navigate = useNavigate(); // React Router navigation function

  const getTenantIdFromToken = (token: string): number | null => {
    try {
      const decoded: TokenPayload = jwtDecode<TokenPayload>(token);
      return decoded.tenant_id || null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const tenantId = getTenantIdFromToken(token) ?? 0;
      setGdriveTenantCredentials((prevDetails) => ({
        ...prevDetails, // Preserve other fields
        tenant_id: tenantId, // Update tenant_id
      })); 
      setSelectedTenant(tenantId);
      if (tenantId) {
        fetchGdriveTenantCredentials(tenantId);
      }
    }
  }, [selectedTenant]); // Fix infinite loop issue

  const fetchGdriveTenantCredentials = async (tenantId: number) => {
    try {
      const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
      const response = await axios.get(`${tenantsapiurl}/${tenantId}/gdrivetenantcredentials`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setGdriveTenantCredentials(response.data);
    } catch (error) {
      console.error("Failed to load credentials", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGdriveTenantCredentials({ ...gdrivetenantcredentials, gdrive_credentials: e.target.value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGdriveTenantCredentials({ ...gdrivetenantcredentials, is_active: e.target.checked });
  };

  const handleSaveGdriveTenantCredentials = async () => {
    try {
      JSON.parse(gdrivetenantcredentials.gdrive_credentials); // Validate JSON
    } catch (error) {
      toast({ title: "Invalid JSON format", status: "error" });
      return;
    }
    gdrivetenantcredentials.id > 0 ? handleUpdateGdriveTenantCredentials() : handleCreateGdriveTenantCredentials();
  };

  const handleCreateGdriveTenantCredentials = async () => {
    try {
      const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
      await axios.post(`${tenantsapiurl}/${selectedTenant}/create-gdrive-credentials`, gdrivetenantcredentials, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast({ title: "Credentials Successfully created" });
    } catch (error) {
      toast({ title: "Error creating credentials", status: "error" });
    }
  };

  const handleUpdateGdriveTenantCredentials = async () => {
    try {
      const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
      await axios.put(`${tenantsapiurl}/${selectedTenant}/update-gdrive-credentials`, gdrivetenantcredentials, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      toast({ title: "Credentials Successfully updated" });
    } catch (error) {
      toast({ title: "Error updating credentials", status: "error" });
    }
  };

  return (
    <Box as="main" mx="auto" p={10}>
      <VStack>
        <Heading size="md">CMS Settings</Heading>
      </VStack>
      <Box mt={4} bgColor={cardbg} borderRadius="sm" p={4} boxShadow="lg">
        <HStack alignItems="center" spacing={4}>
        <Text fontSize="sm" fontWeight="bold" lineHeight="normal">
        Google Drive Credentials - Provide JSON here
        </Text>

        {tenant?.tenant_slug && (
          <Link
            color="blue.500"
            fontSize="sm"
            href={`/platform/${tenant.tenant_slug}/app/gdrive-setup-guide`}
          >
            Read Instructions for Google Drive Setup
          </Link>
        )}
    </HStack>
   
        <VStack mt={4} spacing={4} align="stretch">
        <FormControl id="gdrive_credentials" isRequired>
            <Textarea
                name="gdrive_credentials"
                placeholder="Enter Google Credentials (JSON)"
                size="sm"
                height="150px"  // Increased height for better visibility
                value={gdrivetenantcredentials.gdrive_credentials}
                onChange={(e) => setGdriveTenantCredentials(prev => ({ ...prev, gdrive_credentials: e.target.value }))}
            />
        </FormControl>
          <FormControl id="is_active">
            <Flex align="center" gap={4}>
              <FormLabel minWidth="150px" mb="0" fontSize="sm">Status</FormLabel>
              <Checkbox
                name="is_active"
                isChecked={gdrivetenantcredentials.is_active}
                onChange={handleCheckboxChange}
              >
                Active
              </Checkbox>
            </Flex>


          </FormControl>
        </VStack>
      </Box>
      <Box textAlign="left" mt={4}>
        <Button mb={2} colorScheme="blue" size="sm" borderRadius="sm" onClick={handleSaveGdriveTenantCredentials}>
          Save GDrive Credentials
        </Button>
      </Box>
    </Box>
  );
};

export default TenantCMSMapping;
